import { forwardRef } from 'react';
import { FormInstance } from 'antd';
import {
  type DataObjectGroupsWithDescriptionsMapById,
  DataObjectGroupType,
  DataObjectGroupProcessingStateFilter,
  DirectoryFilter,
  DirectoryFilterProps,
  useDataObjectGroupsWithDescriptionsQuery,
  useGroupDescriptionOptions,
} from '@atlas-frontend/sdk';
import { GroupOrDescriptionSelect } from '../GroupOrDescriptionSelect';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './OutputDataDirectoryTable.translations.json';
import { useMemo } from 'react';

const { addTranslations, useTranslation } = i18nNsFactory(
  'output-data-directory-table',
);
addTranslations(translations);

interface OutputDataDirectoryFilterProps
  extends DirectoryFilterProps<
    Pick<DataObjectGroupProcessingStateFilter, 'outputCode' | 'sourceCode'>
  > {
  initialValues?: Pick<
    DataObjectGroupProcessingStateFilter,
    'outputCode' | 'sourceCode'
  >;
}

export const OutputDataDirectoryFilter = forwardRef<
  FormInstance,
  OutputDataDirectoryFilterProps
>((props, ref) => {
  const { t } = useTranslation();
  const { initialValues, ...rest } = props;
  const { data: groupsWithDescriptions, isFetching } =
    useDataObjectGroupsWithDescriptionsQuery({ active: false });

  const groupsWithDescriptionOptions = useGroupDescriptionOptions(
    groupsWithDescriptions,
  );

  const outputGroupsWithDescriptions = useMemo(() => {
    const outputGroupsWithDescriptionsMap: DataObjectGroupsWithDescriptionsMapById =
      new Map();

    for (const [id, group] of groupsWithDescriptions || []) {
      if (group.groupType === null) {
        outputGroupsWithDescriptionsMap.set(null, {
          ...group,
          descriptions: group.descriptions.filter(
            (desc) => desc.groupType === DataObjectGroupType.O,
          ),
        });
      }
      if (group.groupType === DataObjectGroupType.O) {
        outputGroupsWithDescriptionsMap.set(id, group);
      }
    }

    return outputGroupsWithDescriptionsMap;
  }, [groupsWithDescriptions]);
  const outputGroupsWithDescriptionOptions = useGroupDescriptionOptions(
    outputGroupsWithDescriptions,
  );

  const filterFields = useMemo(
    () => [
      {
        name: 'sourceCode',
        label: t('menu.sources'),
        initialValue: initialValues?.sourceCode,
        input: () => (
          <GroupOrDescriptionSelect
            options={groupsWithDescriptionOptions}
            isLoading={isFetching}
          />
        ),
      },
      {
        name: 'outputCode',
        label: t('menu.output'),
        initialValue: initialValues?.outputCode,
        input: () => (
          <GroupOrDescriptionSelect
            options={outputGroupsWithDescriptionOptions}
            isLoading={isFetching}
          />
        ),
      },
    ],
    [
      groupsWithDescriptionOptions,
      initialValues?.outputCode,
      initialValues?.sourceCode,
      isFetching,
      outputGroupsWithDescriptionOptions,
      t,
    ],
  );

  return <DirectoryFilter ref={ref} fields={filterFields} {...rest} />;
});
